import * as Sentry from "@sentry/react";
import { useCallback, useEffect, useRef } from "react";
import { captureApolloError } from "services/external/sentry";
import type { Fridge } from "types";
import { networkTrace, shouldTraceNetworkDebug } from "utils/debug/networkTrace";
import { INITIAL_STATE_QUERY } from "utils/gqlOperations";
import { logToSentry } from "utils/logger";

type ReconnectArgs = {
	isSubscriptionBroken: boolean;
	appConnected: boolean;
	fridgeId: string;
	refetch: () => Promise<{ data?: { fridge?: Fridge | null } }>;
	onData: (fridge: Fridge) => void;
	/**
	 * Re-subscribes the live FRIDGE_SUBSCRIPTION (Apollo useSubscription's restart()).
	 * The AppSync link errors the observable on a dropped socket and never re-opens it, and
	 * RetryLink only retries a few times before giving up — so on recovery the refetch below
	 * restores initial state but the live stream stays dead unless we restart it here too.
	 */
	restartSubscription: () => void;
};

/**
 * Owns the "re-fetch initial fridge state after the live subscription broke" retry.
 *
 * Defects fixed vs. the old inline DeviceContext logic:
 * - The retry setTimeout is tracked in a ref and cleared on cleanup, so it can't fire after unmount.
 * - The reconnect function is stabilized with useCallback and guarded by isReconnectingRef, so the
 *   effect runs one retry chain per outage instead of re-firing on every provider re-render.
 * - onData is kept current via a ref, so a fresh onData identity each render does NOT re-trigger the
 *   reconnect effect.
 */
export const useReconnectAfterBrokenSubscription = ({
	isSubscriptionBroken,
	appConnected,
	fridgeId,
	refetch,
	onData,
	restartSubscription,
}: ReconnectArgs): void => {
	const onDataRef = useRef(onData);
	const restartSubscriptionRef = useRef(restartSubscription);
	const retryTimeoutRef = useRef<number | null>(null);
	const isReconnectingRef = useRef(false);
	// Tracks the previous appConnected so the effect below can detect the false→true rising edge.
	const prevAppConnectedRef = useRef(appConnected);

	useEffect(() => {
		onDataRef.current = onData;
	}, [onData]);

	// Kept in a ref so a fresh restartSubscription identity each render (it depends on
	// shouldSkipSubscription in the provider) does NOT re-trigger the recovery effect.
	useEffect(() => {
		restartSubscriptionRef.current = restartSubscription;
	}, [restartSubscription]);

	const handleReconnectAfterBrokenSubscription = useCallback(
		async (retryCount: number): Promise<void> => {
			if (retryCount > 2) {
				captureApolloError(new Error("Failed to load initial fridge state 3 times"), {
					kind: "Initial_Query_Error_Retries_Limit",
					query: INITIAL_STATE_QUERY.kind,
					variables: { fridgeId: fridgeId },
				});
				return;
			}
			try {
				if (shouldTraceNetworkDebug()) {
					networkTrace(`[ws] reconnect: refetch attempt #${retryCount + 1}`);
				}
				logToSentry(`Sending initial query after broken subscription on fridge: ${fridgeId}`, "warning");
				const res = await refetch();
				if (res.data?.fridge) {
					if (shouldTraceNetworkDebug()) {
						networkTrace("[ws] reconnect: refetch ok → initial state restored");
					}
					logToSentry(`Setting initial data after broken subscription fridge: ${fridgeId}`, "warning", [
						res.data.fridge,
					]);
					onDataRef.current(res.data.fridge);
				} else {
					if (shouldTraceNetworkDebug()) {
						networkTrace(`[ws] reconnect: refetch empty (retry #${retryCount + 1})`);
					}
					captureApolloError(
						new Error("Initial query error"),
						{
							kind: "Initial_Query_Error",
							query: INITIAL_STATE_QUERY.kind,
							variables: { fridgeId: fridgeId },
						},
						{
							ResponseData: res,
						},
					);
					retryTimeoutRef.current = window.setTimeout(() => {
						handleReconnectAfterBrokenSubscription(retryCount + 1);
					}, 1000);
				}
			} catch (err) {
				Sentry.captureException(err);
			}
		},
		[fridgeId, refetch],
	);

	useEffect(() => {
		if (isSubscriptionBroken && appConnected && fridgeId && !isReconnectingRef.current) {
			isReconnectingRef.current = true;
			// Resume the live stream (a new AppSync handshake); the first frame that arrives
			// clears isSubscriptionBroken via onData. Done before/independent of the refetch so
			// the live subscription recovers even if the initial-query refetch is still retrying.
			if (shouldTraceNetworkDebug()) {
				networkTrace("[ws] reconnect: restart subscription");
			}
			restartSubscriptionRef.current();
			handleReconnectAfterBrokenSubscription(0);
		}
		if (!isSubscriptionBroken) {
			isReconnectingRef.current = false;
		}
	}, [isSubscriptionBroken, appConnected, fridgeId, handleReconnectAfterBrokenSubscription]);

	// Connectivity recovered (appConnected false→true) without a FridgeSubscription error having flipped
	// isSubscriptionBroken — e.g. a non-subscription op tripped offline, or the socket recovered without
	// surfacing an observable error. We may have missed sparse deltas during the gap (notably the BE's
	// post-transaction QR rotation, a one-shot frame), so re-pull authoritative state via the same
	// network-only refetch the broken-subscription path uses. Refetch ONLY: the live stream wasn't
	// necessarily broken, so we must not force an AppSync re-handshake here (restart stays owned by the
	// isSubscriptionBroken branch above). A redundant network-only refetch is idempotent, so this is
	// deliberately NOT suppressed while isSubscriptionBroken is true — that also covers the case where
	// isSubscriptionBroken stays stuck true on a long-idle fridge (it only clears on the next subscription
	// frame), which would otherwise leave the isReconnectingRef-guarded branch unable to re-fire.
	useEffect(() => {
		const regainedConnectivity = !prevAppConnectedRef.current && appConnected;
		prevAppConnectedRef.current = appConnected;
		if (regainedConnectivity && fridgeId) {
			if (shouldTraceNetworkDebug()) {
				networkTrace("[ws] reconnect: connectivity regained → refetch");
			}
			handleReconnectAfterBrokenSubscription(0);
		}
	}, [appConnected, fridgeId, handleReconnectAfterBrokenSubscription]);

	useEffect(
		() => () => {
			if (retryTimeoutRef.current !== null) {
				window.clearTimeout(retryTimeoutRef.current);
				retryTimeoutRef.current = null;
			}
		},
		[],
	);
};
